import React, { useState, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { corporateTheme } from '../themes/corporate';

const NavContainer = styled(motion.nav)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  padding: 1rem 0;
  background: ${props => props.scrolled ? 'rgba(255, 255, 255, 0.95)' : 'transparent'};
  backdrop-filter: ${props => props.scrolled ? 'blur(20px)' : 'none'};
  border-bottom: ${props => props.scrolled ? `1px solid ${corporateTheme.cardBorder}` : 'none'};
  transition: all 0.3s ease;
  width: 100%;
  box-shadow: ${props => props.scrolled ? corporateTheme.cardShadow : 'none'};
`;

const NavContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 2rem;
  width: 100%;
  
  @media (max-width: 768px) {
    padding: 0 1rem;
  }
  
  @media (max-width: 480px) {
    padding: 0 0.5rem;
  }
`;

const Logo = styled(motion.div)`
  font-size: 1.8rem;
  font-weight: ${corporateTheme.fontWeights.bold};
  background: ${corporateTheme.primaryGradient};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  cursor: pointer;
  font-family: ${corporateTheme.fontFamily};
  
  @media (max-width: 768px) {
    font-size: 1.5rem;
  }
  
  @media (max-width: 480px) {
    font-size: 1.3rem;
  }
`;

const NavLinks = styled.div`
  display: flex;
  gap: 2rem;
  align-items: center;
  
  @media (max-width: 1024px) {
    gap: 1.5rem;
  }
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const NavLink = styled(motion.a)`
  color: ${corporateTheme.textPrimary};
  text-decoration: none;
  font-weight: ${corporateTheme.fontWeights.medium};
  cursor: pointer;
  position: relative;
  padding: 0.5rem 1rem;
  border-radius: ${corporateTheme.borderRadius.lg};
  transition: all 0.3s ease;
  font-size: 0.9rem;
  font-family: ${corporateTheme.fontFamily};
  
  &:hover {
    color: ${corporateTheme.primary};
    background: rgba(37, 99, 235, 0.1);
  }
  
  &.active {
    color: ${corporateTheme.primary};
    background: rgba(37, 99, 235, 0.15);
  }
  
  @media (max-width: 1024px) {
    font-size: 0.8rem;
    padding: 0.4rem 0.8rem;
  }
`;

const MobileMenuButton = styled(motion.button)`
  display: none;
  background: none;
  border: none;
  color: ${corporateTheme.textPrimary};
  font-size: 1.5rem;
  cursor: pointer;
  padding: 0.5rem;
  
  @media (max-width: 768px) {
    display: block;
  }
`;

const MobileMenu = styled(motion.div)`
  position: fixed;
  top: 80px;
  left: 0;
  right: 0;
  background: rgba(255, 255, 255, 0.98);
  backdrop-filter: blur(20px);
  padding: 2rem;
  border-top: 1px solid ${corporateTheme.cardBorder};
  z-index: 999;
  box-shadow: ${corporateTheme.cardShadow};
  
  @media (max-width: 480px) {
    padding: 1rem;
    top: 70px;
  }
`;

const MobileNavLink = styled(motion.a)`
  display: block;
  color: ${corporateTheme.textPrimary};
  text-decoration: none;
  font-weight: ${corporateTheme.fontWeights.medium};
  cursor: pointer;
  padding: 1rem 0;
  border-bottom: 1px solid ${corporateTheme.cardBorder};
  transition: all 0.3s ease;
  font-family: ${corporateTheme.fontFamily};
  
  &:hover {
    color: ${corporateTheme.primary};
  }
  
  &:last-child {
    border-bottom: none;
  }
`;

const ProgressBar = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  height: 3px;
  background: ${corporateTheme.primaryGradient};
  z-index: 1001;
  transform-origin: left;
`;

const Navigation = () => {
  const [scrolled, setScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState('hero');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);

  const navItems = useMemo(() => [
    { id: 'hero', label: 'Home' },
    { id: 'executive-summary', label: 'Summary' },
    { id: 'market-analysis', label: 'Market' },
    { id: 'business-model', label: 'Business' },
    { id: 'strategy', label: 'Strategy' },
    { id: 'financials', label: 'Financials' },
    { id: 'timeline', label: 'Timeline' },
    { id: 'team', label: 'Team' },
    { id: 'contact', label: 'Contact' }
  ], []);

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.pageYOffset;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      const progress = scrollTop / docHeight;
      
      setScrolled(scrollTop > 50);
      setScrollProgress(progress);

      // Update active section based on scroll position
      const sections = navItems.map(item => document.getElementById(item.id));
      const currentSection = sections.find(section => {
        if (section) {
          const rect = section.getBoundingClientRect();
          return rect.top <= 100 && rect.bottom >= 100;
        }
        return false;
      });

      if (currentSection) {
        setActiveSection(currentSection.id);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [navItems]);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }
    setMobileMenuOpen(false);
  };

  return (
    <>
      <ProgressBar
        style={{ scaleX: scrollProgress }}
        initial={{ scaleX: 0 }}
        animate={{ scaleX: scrollProgress }}
        transition={{ duration: 0.3 }}
      />
      
      <NavContainer
        scrolled={scrolled}
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <NavContent>
          <Logo
            onClick={() => scrollToSection('hero')}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            GrowTech
          </Logo>

          <NavLinks>
            {navItems.map((item) => (
              <NavLink
                key={item.id}
                onClick={() => scrollToSection(item.id)}
                className={activeSection === item.id ? 'active' : ''}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                {item.label}
              </NavLink>
            ))}
          </NavLinks>

          <MobileMenuButton
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            whileTap={{ scale: 0.95 }}
          >
            ☰
          </MobileMenuButton>
        </NavContent>
      </NavContainer>

      <AnimatePresence>
        {mobileMenuOpen && (
          <MobileMenu
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
          >
            {navItems.map((item, index) => (
              <MobileNavLink
                key={item.id}
                onClick={() => scrollToSection(item.id)}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                {item.label}
              </MobileNavLink>
            ))}
          </MobileMenu>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navigation; 